import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";

import {
  StzhPopoverClosedEvent,
  StzhPopoverCloseEvent,
  StzhPopoverOpenedEvent,
  StzhPopoverOpenEvent,
} from "../../index";

import { arrow, autoUpdate, computePosition, flip, offset, Placement, shift } from "@floating-ui/dom";

import { createFocusTrap, FocusTrap } from "focus-trap";
import { isStzhElement } from "../../utils/utils";
import { addMediaChangeListener, media, removeMediaChangeListener } from "../../utils/media-utils";
import { waitForEvent } from "../../utils/event-utils";
import { animateTo, stopAnimations } from "../../utils/animation-utils";

import { StzhPopoverLocalizedText } from "./stzh-popover.localization";

import Arrow from "../stzh-tooltip/assets/arrow.svg";

const ANIMATION_SHOW_DURATION = 200;
const ANIMATION_HIDE_DURATION = 300;
const ANIMATION_SHOW_DURATION_SMALL = 100;
const ANIMATION_HIDE_DURATION_SMALL = 200;

function animationContentWrapperShow() {
  const isSmall = media("small").matches;
  return {
    keyframes: [{ opacity: "0" }, { opacity: "1" }],
    options: {
      duration: isSmall ? ANIMATION_SHOW_DURATION_SMALL : ANIMATION_SHOW_DURATION,
      easing: "linear",
    },
  };
}

function animationContentWrapperHide() {
  const isSmall = media("small").matches;
  return {
    keyframes: [{ opacity: "1" }, { opacity: "0" }],
    options: {
      duration: isSmall ? ANIMATION_HIDE_DURATION_SMALL : ANIMATION_HIDE_DURATION,
      easing: "linear",
    },
  };
}

function animationDialogShow() {
  const isSmall = media("small").matches;
  return {
    keyframes: [{ transform: isSmall ? "translateY(-8px)" : "translateY(100%)" }, { transform: "translateY(0px)" }],
    options: {
      duration: isSmall ? ANIMATION_SHOW_DURATION_SMALL : ANIMATION_SHOW_DURATION,
      easing: "linear",
    },
  };
}

function animationDialogHide() {
  const isSmall = media("small").matches;
  return {
    keyframes: [{ transform: "translateY(0px)" }, { transform: isSmall ? "translateY(-8px)" : "translateY(100%)" }],
    options: {
      duration: isSmall ? ANIMATION_HIDE_DURATION_SMALL : ANIMATION_HIDE_DURATION,
      easing: "linear",
    },
  };
}

const keyCode = {
  ESC: 27,
};

const CLASS_BODY_OPEN = "stzh-popover-open";

let popoverCounter = 0;

/**
 * @slot - Trigger element for popover
 * @slot content - Any element used as popover content
 * @slot action - `stzh-button` element
 * @slot label - Label for mobile view (alternative for label property)
 */
@Component({
  tag: "stzh-popover",
  styleUrl: "stzh-popover.scss",
  scoped: true,
})
export class StzhPopover {
  /** Whether popover should be open */
  @Prop({ mutable: true, reflect: true }) open: boolean = false;

  /** Default placement of popover relative to trigger element */
  @Prop({ reflect: true }) placement: Placement = "bottom";

  /** Whether to stretch popover to fullwith of parent */
  @Prop({ reflect: true }) fullwidth: "horizontal" | "vertical" | "" = "";

  /** Variant */
  @Prop({ reflect: true }) variant: "default" | "secondary" | "tooltip" = "default";

  /** Size */
  @Prop({ reflect: true }) size: "default" | "large" = "default";

  /** Shifting of popover content */
  @Prop() skidding: number = 0;

  /** Distance from popover content to trigger. "8" when default variant, "16" when tooltip variant, arrow height (8px) + spacing (4px) = 12. */
  @Prop() distance: number;

  /** Label for mobile view (use label slot as alternative) */
  @Prop() label: string = "";

  /** Position strategy */
  @Prop() strategy: "absolute" | "fixed" = "absolute";

  /** Translation strings. */
  @Prop() localization: StzhPopoverLocalizedText;

  @Element() element: HTMLStzhPopoverElement;

  /** Popover open event */
  @Event() stzhOpen: EventEmitter<StzhPopoverOpenEvent>;

  /** Popover opened event (after animation) */
  @Event() stzhOpened: EventEmitter<StzhPopoverOpenedEvent>;

  /** Popover close event */
  @Event() stzhClose: EventEmitter<StzhPopoverCloseEvent>;

  /** Popover closed event (after animation) */
  @Event() stzhClosed: EventEmitter<StzhPopoverClosedEvent>;

  private initialParentNode: Node;
  private elementIndexOnBranchout: number;

  /** Show popover content */
  @Method()
  async show() {
    if (this.open) {
      return;
    }

    this.mediaChangeHandler();
    if (this.isHeaderStuckAndViewportMicro) {
      this.branchOutToBody();
    }

    this.toggledByMethod = true;
    this.open = true;
    return waitForEvent(this.element, "stzhOpened");
  }

  /** Hide popover content */
  @Method()
  async hide() {
    if (!this.open) {
      return;
    }

    this.toggledByMethod = true;
    this.open = false;

    await waitForEvent(this.element, "stzhClosed");

    this.mediaChangeHandler();
    if (this.isHeaderStuckAndViewportMicro) {
      this.branchInFromBody();
    }

    return true;
  }

  /** Toggle popover content */
  @Method()
  async toggle() {
    if (this.open) {
      return await this.hide();
    } else {
      return await this.show();
    }
  }

  @Method()
  async update() {
    return await this.computePosition();
  }

  @Listen("click", { target: "document", capture: true })
  handleOutsideClick(event: MouseEvent) {
    if (!this.open) {
      return;
    }

    const isClickOutside =
      event.target !== this.dialogElement && this.dialogElement.contains(event.target as HTMLElement) === false;

    const isClickTrigger =
      event.target === this.triggerElement || this.triggerElement.contains(event.target as HTMLElement);

    if (isClickOutside && !isClickTrigger) {
      this.hide();
    }
  }

  @Watch("open")
  async openWatcher(newValue: boolean) {
    if (!this.dialogElement) {
      return;
    }

    if (newValue) {
      if (this.toggledByMethod) {
        this.stzhOpen.emit({
          component: "stzh-popover",
        });
      }

      const animationShow = animationContentWrapperShow();
      const animationShowDialog = animationDialogShow();

      await Promise.all([
        stopAnimations(this.contentWrapperElement),
        stopAnimations(this.contentElement),
        stopAnimations(this.dialogElement),
      ]);

      // safari seems to need will-change,
      // otherwise it has problems animating the element with the drop shadow
      this.dialogElement.style.willChange = "transform, opacity, position, top, left, bottom, right";
      this.contentWrapperElement.hidden = false;

      await Promise.all([
        animateTo(
          media("small").matches ? this.dialogElement : this.contentWrapperElement,
          animationShow.keyframes,
          animationShow.options,
        ),
        animateTo(
          media("small").matches ? this.dialogElement : this.contentElement,
          animationShowDialog.keyframes,
          animationShowDialog.options,
        ),
      ]);

      if (this.toggledByMethod) {
        this.stzhOpened.emit({
          component: "stzh-popover",
        });
      }
    } else {
      if (this.toggledByMethod) {
        this.stzhClose.emit({
          component: "stzh-popover",
        });
      }

      const animationHide = animationContentWrapperHide();
      const animationHideDialog = animationDialogHide();

      await Promise.all([
        stopAnimations(this.contentWrapperElement),
        stopAnimations(this.contentElement),
        stopAnimations(this.dialogElement),
      ]);

      await Promise.all([
        animateTo(
          media("small").matches ? this.dialogElement : this.contentWrapperElement,
          animationHide.keyframes,
          animationHide.options,
        ),
        animateTo(
          media("small").matches ? this.dialogElement : this.contentElement,
          animationHideDialog.keyframes,
          animationHideDialog.options,
        ),
      ]);

      this.contentWrapperElement.hidden = true;
      this.dialogElement.style.willChange = "auto";

      if (this.toggledByMethod) {
        this.stzhClosed.emit({
          component: "stzh-popover",
        });
      }
    }

    this.toggledByMethod = false;
  }

  @Watch("distance")
  distanceWatcher(newValue: number) {
    if (typeof newValue === "number") {
      this.distance = newValue;
    } else {
      this.distance = this.variant === "tooltip" ? 12 : 8;
    }
  }

  @State() computedPlacement: Placement;
  @State() isHeaderStuckAndViewportMicro: boolean;
  @State() isBranchedOutToBody: boolean;
  @State() hasStzhHeaderParent: boolean;

  private observer: MutationObserver;

  private contentWrapperElement: HTMLElement;
  private contentElement: HTMLElement;
  private arrowElement: HTMLElement;
  private rootElement: HTMLElement;
  private triggerElement: HTMLElement;
  private triggerFirstElement: HTMLElement;
  private dialogElement: HTMLElement;
  private autoUpdateCleanup: Function;

  private initialTouchX: number = null;
  private initialTouchY: number = null;

  private id: string;
  private trap: FocusTrap;
  private toggledByMethod: boolean;

  private setStzhHeaderParent(): boolean {
    let parent = this.element.parentElement;
    while (parent) {
      if (parent.tagName && parent.tagName.toLowerCase() === "stzh-header") {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  }

  private getChildIndex(element) {
    let parent = element.parentNode;

    // Convert child nodes to an array and find the index of the element
    let index = Array.prototype.indexOf.call(parent.children, element);

    return index;
  }

  private insertElementAtIndex(parent, newElement, index) {
    // Get the child element at the specified index
    let referenceNode = parent.children[index];

    // If referenceNode is null (meaning the index is out of range or at the end), append the new element
    if (referenceNode) {
      parent.insertBefore(newElement, referenceNode);
    } else {
      parent.appendChild(newElement);
    }
  }

  private mediaChangeHandler = () => {
    const isStzhHeaderStuck = document.documentElement.style.getPropertyValue("--stzh-header-is-stuck");
    this.isHeaderStuckAndViewportMicro =
      !media("small").matches && isStzhHeaderStuck === "1" && this.hasStzhHeaderParent;

    if (this.open && this.isBranchedOutToBody && !this.isHeaderStuckAndViewportMicro) {
      this.branchInFromBody();
    }
  };

  private branchOutToBody() {
    this.elementIndexOnBranchout = this.getChildIndex(this.element);
    document.body.append(this.element);
    this.insertElementAtIndex(this.initialParentNode, this.triggerElement, this.elementIndexOnBranchout);
    this.isBranchedOutToBody = true;
  }

  private branchInFromBody() {
    this.insertElementAtIndex(this.initialParentNode, this.element, this.elementIndexOnBranchout);
    this.rootElement.prepend(this.triggerElement);
    this.isBranchedOutToBody = false;
  }

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.keyCode === keyCode.ESC) {
      this.hide();
    }
  };

  private onClickTrigger = () => {
    this.toggle();
  };

  private computePosition = async () => {
    if (media("small").matches) {
      const middleware = [
        offset({
          mainAxis: this.distance,
          crossAxis: this.skidding,
        }),
        flip({
          padding: 5,
        }),
        shift({
          padding: 5,
        }),
      ];

      if (this.variant === "tooltip") {
        middleware.push(
          arrow({
            element: this.arrowElement,
            padding: 5,
          }),
        );
      }

      const {
        x = 0,
        y = 0,
        strategy = "absolute",
        placement,
        middlewareData,
      } = await computePosition(this.triggerFirstElement, this.dialogElement, {
        strategy: this.strategy,
        placement: this.placement,
        middleware,
      });

      this.computedPlacement = placement;

      Object.assign(this.dialogElement.style, {
        position: strategy,
        left: `${x}px`,
        top: `${y}px`,
      });

      if (this.variant === "tooltip") {
        Object.assign(this.arrowElement.style, {
          left: x != null ? `${middlewareData.arrow.x}px` : "",
          top: y != null ? `${middlewareData.arrow.y}px` : "",
        });
      }
    } else {
      Object.assign(this.dialogElement.style, {
        position: null,
        left: null,
        top: null,
      });

      if (this.variant === "tooltip") {
        Object.assign(this.arrowElement.style, {
          left: null,
          top: null,
        });
      }
    }
  };

  private handleTouchStart = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    this.initialTouchX = touch.pageX;
    this.initialTouchY = touch.pageY;
  };

  // private handleTouchMove = (event: TouchEvent) => {
  //   event.preventDefault()
  // }

  private handleTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    const distX = touch.pageX - this.initialTouchX; // get horizontal dist traveled
    const distY = touch.pageY - this.initialTouchY; // get vertical dist traveled
    const threshold = 70;

    const isYSwipe = Math.abs(distY) >= threshold && Math.abs(distX) <= threshold;

    if (isYSwipe) {
      const isInDropdown = (event.target as HTMLElement).closest(".stzh-dropdown") !== null;
      if (!isInDropdown) {
        this.hide();
      }
    }

    this.initialTouchY = null;
    this.initialTouchX = null;
  };

  private init = () => {
    let trigger = this.triggerElement?.firstElementChild as HTMLElement;

    if (!trigger) {
      const defaultSlot = Array.from(this.element.children).find(child => !child.hasAttribute("slot")) as HTMLElement;

      trigger = defaultSlot;
    }

    if (trigger) {
      this.triggerFirstElement = trigger;
      trigger.addEventListener("click", this.onClickTrigger);
    }
  };

  async componentWillLoad() {
    this.id = `stzh-popover-${popoverCounter++}`;
    this.initialParentNode = this.element.parentNode;
    this.hasStzhHeaderParent = this.setStzhHeaderParent();

    this.distanceWatcher(this.distance);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "popover");
    }
  }

  private popoverShown() {
    document.body.classList.add(CLASS_BODY_OPEN);

    const isStzh = isStzhElement(this.triggerFirstElement);

    this.triggerFirstElement.setAttribute(isStzh ? "a11y-expanded" : "aria-expanded", "true");

    if (this.trap) {
      this.trap.activate();
    }
  }

  private popoverHidden() {
    document.body.classList.remove(CLASS_BODY_OPEN);

    const isStzh = isStzhElement(this.triggerFirstElement);

    this.triggerFirstElement.setAttribute(isStzh ? "a11y-expanded" : "aria-expanded", "false");

    if (this.trap) {
      this.trap.deactivate();
    }
  }

  componentDidRender() {
    this.mediaChangeHandler();
    const isStzh = isStzhElement(this.triggerFirstElement);

    if (
      (isStzh && !this.triggerFirstElement.getAttribute("a11y-describedby")) ||
      (!isStzh && !this.triggerFirstElement.getAttribute("aria-describedby"))
    ) {
      this.triggerFirstElement.setAttribute(
        isStzh ? "a11y-describedby" : "aria-describedby",
        `${this.id}-trigger-description`,
      );
    }

    if (
      (isStzh && !this.triggerFirstElement.getAttribute("a11y-label")) ||
      (!isStzh && !this.triggerFirstElement.getAttribute("aria-label"))
    ) {
      this.triggerFirstElement.setAttribute(isStzh ? "a11y-label" : "aria-label", this.label);
    }

    if (
      (isStzh && !this.triggerFirstElement.getAttribute("analytics-id")) ||
      (!isStzh && !this.triggerFirstElement.getAttribute("s-object-id"))
    ) {
      this.triggerFirstElement.setAttribute(isStzh ? "analytics-id" : "s-object-id", this.label);
    }
  }

  private initializedPopover = false;

  componentDidUpdate() {
    if (this.open && !this.initializedPopover) {
      this.initializeAutoUpdate();
      this.initializeFocusTrap();
      this.popoverShown();
      this.initializedPopover = true;
    }

    if (this.initializedPopover) {
      if (this.open) {
        this.popoverShown();
      } else {
        this.popoverHidden();
      }

      this.computePosition();
      if (this.trap) {
        this.trap.updateContainerElements(this.dialogElement);
      }
    }
  }

  initializeAutoUpdate() {
    this.autoUpdateCleanup = autoUpdate(this.triggerFirstElement, this.dialogElement, this.computePosition);
  }

  initializeFocusTrap() {
    this.trap = createFocusTrap(this.dialogElement, {
      initialFocus: () => this.dialogElement,
      fallbackFocus: this.dialogElement,
      clickOutsideDeactivates: true,
      returnFocusOnDeactivate: true,
    });
  }

  componentDidLoad() {
    this.contentWrapperElement.hidden = !this.open;
    this.open ? this.popoverShown() : this.popoverHidden();
  }

  connectedCallback() {
    addMediaChangeListener(this.mediaChangeHandler);
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    removeMediaChangeListener(this.mediaChangeHandler);
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.autoUpdateCleanup) {
      this.autoUpdateCleanup();
    }

    if (this.triggerFirstElement) {
      this.triggerFirstElement.removeEventListener("click", this.onClickTrigger);
    }
  }

  render() {
    const classes = {
      "stzh-popover": true,
      [`stzh-popover--open`]: this.open,
      [`stzh-popover--placement-${this.computedPlacement}`]: !!this.computedPlacement,
      [`stzh-popover--fullwidth-${this.fullwidth}`]: !!this.fullwidth,
      [`stzh-popover--${this.size}`]: !!this.size,
      [`stzh-popover--${this.variant}`]: !!this.variant,
    };

    return (
      <Host>
        <div
          class={classes}
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
          ref={el => (this.rootElement = el as HTMLDivElement)}
        >
          <div ref={el => (this.triggerElement = el as HTMLDivElement)} class="stzh-popover__trigger">
            <slot></slot>
          </div>
          <div class="stzh-popover__vhidden" id={`${this.id}-trigger-description`}>
            {this.localization.open}
          </div>
          <div ref={el => (this.contentWrapperElement = el as HTMLDivElement)} class="stzh-popover__content-wrapper">
            <div class="stzh-popover__backdrop"></div>
            <div ref={el => (this.contentElement = el as HTMLDivElement)} class="stzh-popover__content">
              <div
                ref={el => (this.dialogElement = el as HTMLDivElement)}
                class="stzh-popover__dialog"
                role="dialog"
                tabindex="-1"
                aria-modal="true"
                aria-labelledby={this.label ? `${this.id}-label` : null}
                aria-hidden={this.open ? "false" : "true"}
                onKeyDown={this.handleKeydown}
              >
                {this.variant === "tooltip" && (
                  <div
                    class="stzh-popover__arrow"
                    ref={el => (this.arrowElement = el as HTMLDivElement)}
                    innerHTML={Arrow}
                  ></div>
                )}
                <div class="stzh-popover__content-inner">
                  <div class="stzh-popover__label-wrapper">
                    <h2 id={`${this.id}-label`} class="stzh-popover__label">
                      {this.label ? this.label : <slot name="label"></slot>}
                    </h2>
                    <button class="stzh-popover__close" onClick={() => this.hide()} type="button">
                      <stzh-icon name="close" class="stzh-popover__icon"></stzh-icon>
                      <span class="stzh-popover__vhidden">{this.localization.close}</span>
                    </button>
                  </div>
                  <div class="stzh-popover__content-slot-wrapper">
                    <div class="stzh-popover__content-slot">
                      <slot name="content"></slot>
                    </div>
                    <div class="stzh-popover__action-slot">
                      <slot name="action"></slot>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
